import type {
  PipelineHistoryField,
  PipelineHistoryFieldChange,
  PreviewIngestionPipelineUpdateResponse,
} from '@repo/lakehouse-sdk';

/**
 * What the update preview says about a partial update. It checks the update
 * against the stream as it is now, so every outcome holds only at that time.
 */
export type UpdatePreviewOutcome =
  | {
      kind: 'changes';
      changes: PipelineHistoryFieldChange[];
      after: PipelineHistoryField[];
      hasUnlistedChanges: boolean;
    }
  // Streams that history does not cover (FLINK) have no diff.
  | { kind: 'diffUnavailable' }
  | { kind: 'rejected'; message: string }
  // The stream is gone, or the gateway does not route the preview yet.
  | { kind: 'notFound' }
  | { kind: 'forbidden' }
  | { kind: 'failed'; message: string };

// The statuses the update itself would answer for this body against the
// stream's current state, except a 422 with one of these codes: lakehouse sends
// them when a check could not run, which says nothing about the body.
const REJECTED_STATUSES = new Set([400, 409, 422]);
const CHECK_UNAVAILABLE_CODES = new Set([
  'UdfCompilerUnavailable',
  'ProbeUnavailable',
]);

// The SDK throws with the HTTP status as `code` and the response text as
// `body`, except for a status the spec declares without a schema: there `body`
// is unset and the response is left unread in `cause`.
interface SdkFailure {
  code?: unknown;
  body?: unknown;
  cause?: { body?: { text?: () => Promise<string> } };
}

const responseTextOf = async ({ body, cause }: SdkFailure): Promise<string> => {
  if (typeof body === 'string') {
    return body;
  }
  try {
    return (await cause?.body?.text?.()) ?? '';
  } catch {
    return '';
  }
};

// Lakehouse errors carry `{ detail, error: { code } }`; any other answer is shown as sent.
const readError = (text: string): { message: string; code: string | null } => {
  let payload: unknown = null;
  try {
    payload = JSON.parse(text);
  } catch {
    // Not JSON: the text is the message.
  }
  if (!payload || typeof payload !== 'object') {
    return { message: text, code: null };
  }
  const detail =
    'detail' in payload && typeof payload.detail === 'string'
      ? payload.detail
      : '';
  const code =
    'error' in payload &&
    payload.error &&
    typeof payload.error === 'object' &&
    'code' in payload.error &&
    typeof payload.error.code === 'string'
      ? payload.error.code
      : null;
  return { message: detail || text, code };
};

export const updatePreviewOutcome = async (
  preview: () => Promise<PreviewIngestionPipelineUpdateResponse>
): Promise<UpdatePreviewOutcome> => {
  let response: PreviewIngestionPipelineUpdateResponse;
  try {
    response = await preview();
  } catch (error) {
    const failure: SdkFailure = error && typeof error === 'object' ? error : {};
    // Not every exception with a numeric `code` came off the wire: a timeout's
    // DOMException carries one too.
    const status =
      typeof failure.code === 'number' &&
      failure.code >= 100 &&
      failure.code <= 599
        ? failure.code
        : null;
    if (status === 403) {
      return { kind: 'forbidden' };
    }
    if (status === 404) {
      return { kind: 'notFound' };
    }
    const { message, code } = readError(await responseTextOf(failure));
    const shown =
      message || (status !== null ? `HTTP ${status}` : 'the request failed');
    return status !== null &&
      REJECTED_STATUSES.has(status) &&
      !(code !== null && CHECK_UNAVAILABLE_CODES.has(code))
      ? { kind: 'rejected', message: shown }
      : { kind: 'failed', message: shown };
  }
  if (!response.diffAvailable) {
    return { kind: 'diffUnavailable' };
  }
  return {
    kind: 'changes',
    changes: response.changes ?? [],
    after: response.after ?? [],
    hasUnlistedChanges: response.hasUnlistedChanges ?? false,
  };
};
